import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BarChart3, Cloud, Shield, Zap } from "lucide-react";

import { LanguageToggle } from "@/components/auth/LanguageToggle";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/lib/i18n";

export function HomePage() {
  const { t } = useTranslation();

  return (
    <div className="relative min-h-screen overflow-hidden bg-background">
      <div className="pointer-events-none absolute inset-0 bg-gradient-mesh opacity-80" />
      <div className="pointer-events-none absolute -left-24 top-28 h-72 w-72 rounded-full bg-primary/20 blur-3xl" />
      <div className="pointer-events-none absolute right-0 top-16 h-80 w-80 rounded-full bg-chart-2/20 blur-3xl" />

      <nav className="fixed top-0 z-50 w-full border-b border-white/40 bg-background/72 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2">
            <div className="center h-10 w-10 rounded-2xl bg-gradient-primary text-primary-foreground shadow-glow">
              <Cloud className="h-5 w-5" />
            </div>
            <span className="font-display text-xl font-bold tracking-tight">Cloudface</span>
          </div>

          <div className="flex items-center gap-4">
            <LanguageToggle />
            <ThemeToggle className="static" />
            <Link to="/login">
              <Button size="sm" className="hidden rounded-full px-5 sm:flex">
                {t("sign_in")}
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <main className="relative z-10 mx-auto max-w-7xl px-4 pb-20 pt-32 sm:px-6 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            <div className="inline-flex items-center rounded-full border border-primary/20 bg-card/80 px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-primary shadow-soft">
              STV Enterprise Solution v2.4.0
            </div>

            <h1 className="text-display max-w-2xl">
              {t("home_hero_title")}
              <span className="block text-gradient"> secure orchestration</span>
            </h1>

            <p className="text-body max-w-xl">{t("home_hero_subtitle")}</p>

            <div className="flex flex-col gap-4 sm:flex-row">
              <Link to="/login">
                <Button
                  size="lg"
                  className="h-12 w-full rounded-full bg-gradient-primary px-8 text-md text-primary-foreground shadow-glow hover:opacity-95 sm:w-auto"
                >
                  {t("access_portal")} <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>

              <Link to="/help">
                <Button
                  size="lg"
                  variant="outline"
                  className="h-12 w-full rounded-full border-white/50 bg-card/70 px-8 text-md shadow-soft sm:w-auto"
                >
                  {t("help_title")}
                </Button>
              </Link>
            </div>

            <div className="flex flex-wrap items-center gap-6 pt-4 text-muted-foreground">
              <div className="flex -space-x-2">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-background bg-secondary text-[10px] font-bold shadow-soft"
                  >
                    U{i}
                  </div>
                ))}
              </div>
              <p className="text-sm font-medium">Trusted by 2k+ global enterprises</p>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <div className="surface-panel p-4">
                <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Availability</p>
                <p className="mt-2 font-display text-2xl font-bold">99.99%</p>
              </div>
              <div className="surface-panel p-4">
                <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Response</p>
                <p className="mt-2 font-display text-2xl font-bold">43 ms</p>
              </div>
              <div className="surface-panel p-4">
                <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Regions</p>
                <p className="mt-2 font-display text-2xl font-bold">27</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative hidden lg:block"
          >
            <div className="surface-panel surface-hero aspect-square rounded-[2rem] p-8">
              <div className="grid h-full grid-cols-2 gap-4">
                <div className="col-span-2 flex items-center justify-between rounded-[1.5rem] border border-white/40 bg-card/85 p-6 shadow-glass">
                  <div className="space-y-1">
                    <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Uptime</p>
                    <p className="text-3xl font-bold">99.99%</p>
                  </div>
                  <BarChart3 className="h-10 w-10 text-primary opacity-50" />
                </div>

                <div className="space-y-2 rounded-[1.5rem] border border-white/35 bg-card/80 p-6 shadow-glass">
                  <Shield className="h-6 w-6 text-primary" />
                  <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Secure</p>
                  <p className="text-sm font-semibold">Zero-trust control plane</p>
                </div>

                <div className="space-y-2 rounded-[1.5rem] border border-white/35 bg-card/80 p-6 shadow-glass">
                  <Zap className="h-6 w-6 text-chart-2" />
                  <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Low Latency</p>
                  <p className="text-sm font-semibold">Edge-first runtime delivery</p>
                </div>

                <div className="col-span-2 rounded-[1.5rem] border border-primary/15 bg-foreground/[0.03] p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Ops Visibility</p>
                      <p className="mt-2 text-lg font-semibold">
                        Policy, audit, agents and tools in one console.
                      </p>
                    </div>
                    <div className="center h-12 w-12 rounded-2xl bg-primary/10">
                      <Cloud className="h-6 w-6 text-primary" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </main>

      <footer className="relative z-10 border-t border-white/40 bg-background/60 py-12 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-8 px-4 sm:px-6 md:flex-row lg:px-8">
          <div className="flex items-center gap-2 opacity-70">
            <Cloud className="h-5 w-5" />
            <span className="font-bold">Cloudface</span>
          </div>

          <div className="flex gap-8 text-sm text-muted-foreground">
            <Link to="/privacy" className="hover:text-foreground">
              Privacy
            </Link>
            <Link to="/terms" className="hover:text-foreground">
              Terms
            </Link>
            <Link to="/help" className="hover:text-foreground">
              Help
            </Link>
            <Link to="/docs" className="hover:text-foreground">
              Docs
            </Link>
          </div>

          <p className="text-xs text-muted-foreground">© 2026 STV Enterprise Solution. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
